import React, { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';

// third-party
import { useDrag, useDragLayer, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

// material-ui
import { styled, Theme, useTheme } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';

// assets
import { CaretDownOutlined, CaretUpOutlined, CloseSquareFilled, DragOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { textSizeByBreakpoint } from '../../../utils/helper';

// ==============================|| HEADER SORT ||============================== //

interface HeaderSortProps {
  column: any;
  sort?: boolean;
}

export const HeaderSort = ({ column, sort }: HeaderSortProps) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ display: 'inline-flex' }}>
      <Box>{column.render('Header')}</Box>
      {!column.disableSortBy && (
        <Stack sx={{ color: 'secondary.light' }} {...(sort && { ...column.getHeaderProps(column.getSortByToggleProps()) })}>
          <CaretUpOutlined
            style={{
              fontSize: '0.625rem',
              color: column.isSorted && !column.isSortedDesc ? theme.palette.text.secondary : 'inherit'
            }}
          />
          <CaretDownOutlined
            style={{
              fontSize: '0.625rem',
              marginTop: -2,
              color: column.isSortedDesc ? theme.palette.text.secondary : 'inherit'
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

// ==============================|| TABLE PAGINATION ||============================== //

interface TablePaginationProps {
  gotoPage: (value: 'next' | 'prev' | number) => void;
  setPageSize: (value: number) => void;
  pageIndex: number;
  pageSize: number;
  dataCount: number;
  rows: any[];
  handlePageSize?: (size: number) => void;
  hidePagePerRow?: boolean;
  cursorBased?: boolean;
  onGotoFirst?: () => void;
}

export const TablePagination = ({
  gotoPage,
  rows,
  setPageSize,
  pageSize,
  pageIndex,
  handlePageSize,
  dataCount,
  hidePagePerRow = false,
  cursorBased = false,
  onGotoFirst
}: TablePaginationProps) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const totalPages = Math.ceil(dataCount / pageSize);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleChange = (event: SelectChangeEvent<number>) => {
    setPageSize(+event.target.value);
    if (handlePageSize) {
      handlePageSize(parseInt(event.target.value.toString()));
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    let startPage = Math.max(0, pageIndex - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Box
          key={i}
          onClick={() => {
            if (i !== pageIndex) {
              gotoPage(i);
            }
          }}
          sx={{
            padding: '5px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: i === pageIndex ? 'transparent' : theme.palette.border.main,
            borderRadius: '4px',
            backgroundColor: i === pageIndex ? theme.palette.background.default : 'transparent',
            color: theme.palette.text.secondary,
            cursor: i === pageIndex ? 'default' : 'pointer',
            fontSize: textSizeByBreakpoint(14),
            fontWeight: 700,
            transition: 'all 0.2s'
          }}
        >
          {i + 1}
        </Box>
      );
    }
    return pages;
  };

  return (
    <Grid container alignItems="center" justifyContent="space-between" sx={{ width: 'auto' }}>
      {!hidePagePerRow && (
        <Grid item>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: textSizeByBreakpoint(14)
              }}
            >
              Result per page
            </Typography>
            <FormControl>
              <Select
                id="demo-controlled-open-select"
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                // @ts-ignore
                value={pageSize}
                onChange={handleChange}
                size="small"
                sx={{
                  minWidth: '66px',
                  height: '36px',
                  border: '1px solid',
                  borderColor: theme.palette.border.main,
                  borderRadius: '8px',
                  backgroundColor: theme.palette.background.paper,
                  '& .MuiSelect-select': {
                    py: '10px',
                    px: '16px',
                    fontSize: textSizeByBreakpoint(14),
                    color: theme.palette.text.primary,
                    fontWeight: 400
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover': {
                    borderColor: theme.palette.border.main
                  },
                  '&.Mui-focused': {
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: textSizeByBreakpoint(14),
                fontWeight: 400
              }}
            >
              {`${pageIndex * pageSize + 1}-${Math.min((pageIndex + 1) * pageSize, dataCount)} of ${dataCount}`}
            </Typography>
          </Stack>
        </Grid>
      )}
      {hidePagePerRow && <Grid item></Grid>}
      <Grid item sx={{ mt: { xs: 2, sm: 0 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* First button for cursor-based pagination - only show when not on first page */}
          {cursorBased && onGotoFirst && pageIndex > 0 && (
            <Box
              onClick={() => onGotoFirst()}
              sx={{
                padding: '5px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                border: '1px solid',
                borderColor: theme.palette.border.main,
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: theme.palette.text.secondary,
                cursor: 'pointer',
                fontSize: textSizeByBreakpoint(14),
                fontWeight: 400,
                transition: 'all 0.2s'
              }}
            >
              <LeftOutlined />
              <LeftOutlined style={{ marginLeft: -8 }} />
              <span>First</span>
            </Box>
          )}
          <Box
            onClick={() => {
              if (pageIndex > 0) {
                gotoPage('prev');
              }
            }}
            sx={{
              padding: '5px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              border: '1px solid',
              borderColor: theme.palette.border.main,
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: theme.palette.text.secondary,
              cursor: pageIndex === 0 ? 'not-allowed' : 'pointer',
              fontSize: textSizeByBreakpoint(14),
              fontWeight: 400,
              opacity: pageIndex === 0 ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
          >
            <LeftOutlined />
            <span>Back</span>
          </Box>

          {/* Only show page numbers for non-cursor based pagination */}
          {!cursorBased && renderPageNumbers()}

          <Box
            onClick={() => {
              if (pageIndex < totalPages - 1) {
                gotoPage('next');
              }
            }}
            sx={{
              padding: '5px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              border: '1px solid',
              borderColor: pageIndex === totalPages - 1 ? theme.palette.border.main : theme.palette.primary.main,
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: pageIndex === totalPages - 1 ? theme.palette.text.secondary : theme.palette.primary.main,
              cursor: pageIndex === totalPages - 1 ? 'not-allowed' : 'pointer',
              fontSize: textSizeByBreakpoint(14),
              fontWeight: 400,
              opacity: pageIndex === totalPages - 1 ? 0.5 : 1,
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: pageIndex === totalPages - 1 ? 'transparent' : theme.palette.primary.main + '10',
                borderColor: pageIndex === totalPages - 1 ? theme.palette.border.main : theme.palette.primary.main
              }
            }}
          >
            <span>Next</span>
            <RightOutlined />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

// ==============================|| SELECTION - PREVIEW ||============================== //

export const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }: { indeterminate: any }, ref: any) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  return <Checkbox indeterminate={indeterminate} ref={resolvedRef} {...rest} />;
});

export const TableRowSelection = ({ selected }: { selected: number }) => (
  <>
    {selected > 0 && (
      <Chip
        size="small"
        // @ts-ignore
        label={`${selected} row(s) selected`}
        color="secondary"
        variant="light"
        sx={{
          position: 'absolute',
          right: -1,
          top: -1,
          borderRadius: '0 4px 0 4px'
        }}
      />
    )}
  </>
);

// ==============================|| DRAG & DROP - DRAGGABLE HEADR ||============================== //

interface DraggableHeaderProps {
  column: any;
  sort?: boolean;
  reorder: (item: any, index: number) => void;
  index: number;
  children: ReactNode;
}

export const DraggableHeader = ({ children, column, index, reorder, sort }: DraggableHeaderProps) => {
  const theme = useTheme();
  const ref = useRef();
  const { id, Header } = column;

  const DND_ITEM_TYPE = 'column';

  const [{ isOverCurrent }, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    drop: (item) => {
      reorder(item, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true })
    })
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: () => ({
      id,
      index,
      header: Header
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(ref));

  let borderColor = theme.palette.text.primary;
  if (isOverCurrent) {
    borderColor = theme.palette.primary.main;
  }

  return (
    <Box sx={{ cursor: 'move', opacity: isDragging ? 0.5 : 1, color: borderColor }} ref={ref} {...isDragging}>
      {children}
    </Box>
  );
};

// ==============================|| DRAG & DROP - DRAG PREVIEW ||============================== //

const DragHeader = styled('div')(({ theme, x, y }: { theme: Theme; x: number; y: number }) => ({
  color: theme.palette.text.secondary,
  position: 'fixed',
  pointerEvents: 'none',
  left: 12,
  top: 24,
  transform: `translate(${x}px, ${y}px)`,
  opacity: 0.6
}));

export const DragPreview = () => {
  const theme = useTheme();
  const { isDragging, item, currentOffset } = useDragLayer((monitor: any) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging()
  }));

  const { x, y } = currentOffset || {};

  return isDragging ? (
    <DragHeader theme={theme} x={x} y={y}>
      {item.header && (
        <Stack direction="row" spacing={1} alignItems="center">
          <DragOutlined style={{ fontSize: '1rem' }} />
          <Typography>{item.header}</Typography>
        </Stack>
      )}
    </DragHeader>
  ) : null;
};

// ==============================|| DRAG & DROP - DRAGGABLE ROW ||============================== //

export const DraggableRow = ({ index, moveRow, children }: any) => {
  const DND_ITEM_TYPE = 'row';

  const dropRef = useRef(null);
  const dragRef = useRef(null);

  const [, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    hover(item: any, monitor: any) {
      if (!dropRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      // @ts-ignore
      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  // @ts-ignore
  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0 : 1;

  preview(drop(dropRef));
  drag(dragRef);

  return (
    <TableRow ref={dropRef} style={{ opacity, backgroundColor: isDragging ? 'red' : 'inherit' }}>
      <TableCell ref={dragRef} sx={{ cursor: 'pointer', textAlign: 'center' }}>
        <DragOutlined />
      </TableCell>
      {children}
    </TableRow>
  );
};

// ==============================|| COLUMN HIDING - SELECT ||============================== //

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200
    }
  }
};

export const HidingSelect = ({ hiddenColumns, setHiddenColumns, allColumns }: any) => {
  const handleChange = (event: SelectChangeEvent<typeof hiddenColumns>) => {
    const {
      target: { value }
    } = event;

    setHiddenColumns(typeof value === 'string' ? value.split(',') : value!);
  };

  return (
    <FormControl sx={{ width: 200 }}>
      <Select
        id="column-hiding"
        multiple
        displayEmpty
        value={hiddenColumns}
        onChange={handleChange}
        input={<OutlinedInput id="select-column-hiding" placeholder="select column" />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <Typography variant="subtitle1">all columns visible</Typography>;
          }

          if (selected.length > 0 && selected.length === allColumns.length) {
            return <Typography variant="subtitle1">all columns hidden</Typography>;
          }

          return <Typography variant="subtitle1">{selected.length} column(s) hidden</Typography>;
        }}
        MenuProps={MenuProps}
        size="small"
      >
        {allColumns.map((column: any, index: number) => (
          <MenuItem key={column.id} value={column.id}>
            <Checkbox
              checked={hiddenColumns!.indexOf(column.id) > -1}
              color="error"
              checkedIcon={
                <Box
                  className="icon"
                  sx={{
                    width: 16,
                    height: 16,
                    border: '1px solid',
                    borderColor: 'inherit',
                    borderRadius: 0.25,
                    position: 'relative'
                  }}
                >
                  <CloseSquareFilled className="filled" style={{ position: 'absolute' }} />
                </Box>
              }
            />
            <ListItemText primary={typeof column.Header === 'string' ? column.Header : column?.title} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// ==============================|| COLUMN SORTING - SELECT ||============================== //

export const SortingSelect = ({ sortBy, setSortBy, allColumns }: any) => {
  const [sort, setSort] = useState(sortBy);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value }
    } = event;
    setSort(value);
    setSortBy([{ id: value, desc: false }]);
  };

  return (
    <FormControl sx={{ width: 200 }}>
      <Select
        id="column-hiding"
        displayEmpty
        value={sort}
        onChange={handleChange}
        input={<OutlinedInput id="select-column-hiding" placeholder="Sort by" />}
        renderValue={(selected) => {
          const selectedColumn = allColumns.filter((column: any) => column.id === selected)[0];
          if (!selected) {
            return <Typography variant="subtitle1">Sort By</Typography>;
          }

          return (
            <Typography variant="subtitle2">
              Sort by ({typeof selectedColumn.Header === 'string' ? selectedColumn.Header : selectedColumn?.title})
            </Typography>
          );
        }}
        size="small"
      >
        {allColumns
          .filter((column: any) => column.canSort)
          .map((column: any) => (
            <MenuItem key={column.id} value={column.id}>
              <ListItemText primary={typeof column.Header === 'string' ? column.Header : column?.title} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
